import { useEffect, useRef, useState } from "react";
import { audio } from "../lib/audio";

/**
 * Attacker-side "juice-jacking" takeover shown when Max charges from an untrusted
 * cable / public USB port. Instead of a 1-frame goblin flash, we SHOW the threat
 * (like the ransomware leak-site cinematic): the USB handshake is accepted, the
 * phone's files start copying to an unknown host with a live progress bar and a
 * scrolling file list, an attacker note ticks up, and a low line is spoken. The
 * learner reads it and clicks Continue -> the teach card.
 */
const FILES = [
  "Contacts.vcf",
  "Photos/ (1,204 items)",
  "WhatsApp/Media/",
  "Downloads/Q3_client_pricing.xlsx",
  "Downloads/NDA_signed.pdf",
  "Keychain/tokens.db",
  "Authenticator/seeds.json",
  "Mail/attachments/",
];

export function ChargeExfil({
  caption,
  line,
  onDone,
}: {
  caption?: string;
  line?: string;
  onDone: () => void;
}) {
  const [pct, setPct] = useState(0);
  const [shown, setShown] = useState<string[]>([]);
  const [ready, setReady] = useState(false);
  const spoke = useRef(false);

  useEffect(() => {
    if (!spoke.current) {
      spoke.current = true;
      // Was voice:"echo" (2026-07-13: switched to "onyx"). "echo" is used
      // elsewhere in this course (WifiPicker/ShoulderSurf) for short,
      // punchy attributed dialogue and works fine there -- but this is a
      // much longer explanatory narration, and echo's more synthetic
      // timbre became noticeably worse over a full paragraph (reported as
      // "sounds very metallic and bad"). onyx is the course's main
      // narrator voice everywhere else and reads long lines clearly.
      void audio.speak(line ?? "Handshake accepted. Pulling everything off the device now.", {
        voice: "onyx",
        lang: "en-US",
      });
    }
    // Sound effects (2026-07-17): this cinematic used to be silent except
    // for the spoken line -- no cue that data was actively streaming out.
    // A low tension pulse runs for the whole card, and each file that pops
    // into the list gets its own "packet sent" blip so the theft reads as
    // an active, ongoing process instead of a static list appearing.
    audio.startExfilAlarm();
    let f = 0;
    const fileTimer = window.setInterval(() => {
      f += 1;
      setShown(FILES.slice(0, f));
      audio.dataBlip();
      if (f >= FILES.length) window.clearInterval(fileTimer);
    }, 420);
    const t0 = performance.now();
    const dur = 4200;
    const progTimer = window.setInterval(() => {
      const k = Math.min(1, (performance.now() - t0) / dur);
      setPct(Math.round(k * 100));
      if (k >= 1) {
        window.clearInterval(progTimer);
        setReady(true);
        audio.stopExfilAlarm();
        audio.breachAlert();
      }
    }, 60);
    return () => {
      window.clearInterval(fileTimer);
      window.clearInterval(progTimer);
      audio.cancelSpeech();
      audio.stopExfilAlarm();
    };
  }, [line]);

  return (
    <div className="cx-wrap" role="alertdialog" aria-label="Device compromised">
      <div className="cx-card">
        <div className="cx-head">
          <span className="cx-dot" /> UNKNOWN DEVICE CONNECTED
        </div>
        <h2 className="cx-title">Data transfer in progress</h2>
        <p className="cx-sub">
          The cable carried DATA, not just power. Max's phone accepted the handshake and is copying
          files to an unknown host.
        </p>

        <div className="cx-dest">
          <span>Copying to</span>
          <code>185.203.116.42:4444 (unknown)</code>
        </div>

        <ul className="cx-files">
          {shown.map((f) => (
            <li key={f}>
              <span className="cx-file-ico" aria-hidden>{"\u2192"}</span>
              {f}
            </li>
          ))}
        </ul>

        <div className="cx-bar" aria-hidden>
          <div className="cx-bar-fill" style={{ width: `${pct}%` }} />
        </div>
        <div className="cx-pct">{pct}% exfiltrated</div>

        {caption && ready && <p className="cx-caption">{caption}</p>}

        <button type="button" className="btn cx-continue" disabled={!ready} onClick={onDone}>
          {ready ? "What just happened?" : "\u2026"}
        </button>
      </div>
    </div>
  );
}
